import { useState, useEffect } from 'react';
import supabase from '../../../lib/supabaseClient';

/**
 * Hook for real system health metrics
 * @param {string} selectedTenantId - The tenant ID to fetch metrics for
 * @returns {object} { healthMetrics, loading, error }
 */
export function useSystemHealth(selectedTenantId = null, channel = 'sms') {
  const [healthMetrics, setHealthMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchHealthMetrics() {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const isAll = selectedTenantId === 'all';

        // Use selected tenant if provided (global_admin), otherwise use auth metadata
        const tenantId = isAll
          ? 'all'
          : (selectedTenantId || user.app_metadata?.tenant_id || user.user_metadata?.tenant_id);

        if (!tenantId) {
          console.error('No tenant_id available');
          return;
        }

        // Check if this is a demo tenant (skip for 'all')
        if (!isAll) {
          const { data: tenantData } = await supabase
            .from('tenants')
            .select('is_demo')
            .eq('id', tenantId)
            .single();

          if (tenantData?.is_demo) {
            console.log('[useSystemHealth] Demo tenant detected - returning demo metrics');
            setHealthMetrics([
              channel === 'web'
                ? { label: 'Active Sessions (24h)', value: '12', percentage: 100, color: '#6366F1' }
                : { label: 'Active Campaigns', value: '8', percentage: 80, color: '#EA580C' }
            ]);
            setLoading(false);
            return;
          }
        }

        let activeCampaignsCount = 0;
        if (channel !== 'web') {
          let campaignQuery = supabase
            .from('campaigns')
            .select('*', { count: 'exact', head: true })
            .eq('is_active', true)
            .eq('archived', false);
          if (!isAll) campaignQuery = campaignQuery.eq('tenant_id', tenantId);
          const { count, error: campaignError } = await campaignQuery;
          if (campaignError) throw campaignError;
          activeCampaignsCount = count || 0;
        }

        let metric;
        if (channel === 'web') {
          let sessionsQuery = supabase
            .from('messages')
            .select('session_id', { count: 'exact', head: true })
            .eq('channel', 'web')
            .gte('timestamp', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString());
          if (!isAll) sessionsQuery = sessionsQuery.eq('tenant_id', tenantId);
          const { count: activeSessions } = await sessionsQuery;

          metric = {
            label: 'Active Sessions (24h)',
            value: String(activeSessions || 0),
            percentage: Math.min((activeSessions || 0) * 10, 100),
            color: (activeSessions || 0) > 0 ? '#6366F1' : '#6B7280'
          };
        } else {
          metric = {
            label: 'Active Campaigns',
            value: String(activeCampaignsCount),
            percentage: Math.min(activeCampaignsCount * 10, 100),
            color: activeCampaignsCount > 0 ? '#16A34A' : '#6B7280'
          };
        }

        setHealthMetrics([metric]);
        setLoading(false);
        setError(null);

      } catch (err) {
        console.error('System health error:', err);
        setError(err.message);
        setLoading(false);

        setHealthMetrics([
          channel === 'web'
            ? { label: 'Active Sessions (24h)', value: '--', percentage: 0, color: '#6B7280' }
            : { label: 'Active Campaigns', value: '--', percentage: 0, color: '#6B7280' }
        ]);
      }
    }

    fetchHealthMetrics();

    // Refresh every 30 seconds
    const interval = setInterval(fetchHealthMetrics, 30000);

    return () => clearInterval(interval);
  }, [selectedTenantId, channel]);

  return { healthMetrics, loading, error };
}

export default useSystemHealth;
